import React, { useState, useEffect } from "react";
import {
  signUp,
  confirmSignUp,
  signIn,
  resendSignUpCode,
  confirmResetPassword,
  fetchAuthSession,
  resetPassword,
  signInWithRedirect,
} from "aws-amplify/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { usePostHog } from "@posthog/react";
import type { AppDispatch, RootState } from "../store";
import {
  authenticationState,
  logOutUser,
  setUserDetails,
} from "../store/userSlice";
import { usePostHogCapture } from "../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../utils/posthogEvents";
import { identifyUserForPostHog } from "../utils/posthogIdentify";
import { captureMarketingParams, getPostAuthDestination } from "../utils/marketingParams";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { ReactComponent as ErodeLogoBlack } from "../assets/images/erode-logo-black.svg";
import windFarmImage from "../assets/images/wind_farm.jpg";

// Shared hero section component for desktop view
const AuthHeroSection: React.FC = () => (
  <div className="hidden lg:flex lg:flex-1 items-center justify-center relative px-4 xl:px-6 py-6 xl:py-8">
    <div className="relative w-full h-full lg:h-[calc(100vh-24px)] rounded-3xl overflow-hidden">
      <img
        src={windFarmImage}
        alt="Wind farm landscape"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-eai-dark/80 via-eai-lt-dark/70 to-gray-900/70"></div>
      <div className="absolute bottom-0 left-0 right-0 p-12 z-10">
        <h1 className="text-5xl font-bold text-white mb-4 max-w-lg">
          It's forecasting, not fortune telling.
        </h1>
        <p className="text-xl text-white/90 max-w-lg">
          Generate predictions, manage risks, and build insights across energy and water assets with custom forecasting and decision tools.
        </p>
      </div>
    </div>
  </div>
);

// Shared mobile header component
const MobileHeader: React.FC = () => (
  <div className="lg:hidden flex flex-col items-center text-center mb-8">
    <ErodeLogoBlack className="w-32 h-16 mb-4" />
    <h1 className="text-3xl font-bold text-gray-900 mb-2">
      It's forecasting, not fortune telling.
    </h1>
    <p className="text-gray-600 max-w-sm">
      Generate predictions, manage risks, and build insights across energy and water assets with custom forecasting and decision tools.
    </p>
  </div>
);

interface ModernAuthenticationProps {}

/**
 * Modern login/signup page (replaces legacy Authentication.tsx flow).
 *
 * TODO:
 * - Wire up Terms & Conditions modal and validation, or remove the placeholder.
 * - Implement or remove Social login / OAuth (currently hidden with "coming soon").
 * - Consider extracting subcomponents (e.g. LoginForm, SignUpForm, ForgotPasswordView,
 *   ResetPasswordView, ConfirmationView) or useReducer for screen state to reduce size.
 * - Consider typing Amplify auth errors or adding a getAuthErrorMessage() helper.
 * - Confirmation screen uses a different hero layout; consider shared layout for sub-flows.
 */
const ModernAuthentication: React.FC<ModernAuthenticationProps> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const posthog = usePostHog();
  const { capture } = usePostHogCapture();
  const user = useSelector((state: RootState) => state.user);

  const initialTab: "signup" | "login" =
    searchParams.get("signup") === "true" ? "signup" : "login";
  const [activeTab, setActiveTab] = useState<"signup" | "login">(initialTab);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [showResetNewPassword, setShowResetNewPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [loginErrors, setLoginErrors] = useState<string[]>([]);
  const [signupErrors, setSignupErrors] = useState<string[]>([]);
  const [signupBlocked, setSignupBlocked] = useState(false);
  const [confirmationErrors, setConfirmationErrors] = useState<string[]>([]);
  const [forgotPasswordErrors, setForgotPasswordErrors] = useState<string[]>([]);
  const [resetPasswordErrors, setResetPasswordErrors] = useState<string[]>([]);
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [confirmationStatus, setConfirmationStatus] = useState("");
  
  const [signupForm, setSignupForm] = useState({
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });
  
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [pendingSignUpCredentials, setPendingSignUpCredentials] = useState({
    email: "",
    password: "",
  });
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState("");
  const [forgotPasswordForm, setForgotPasswordForm] = useState({
    email: "",
  });
  const [resetPasswordForm, setResetPasswordForm] = useState({
    code: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  useEffect(() => {
    // Stash any marketing deep-link params (plan/interval/prompt) before auth navigates away.
    // Stored in sessionStorage so they survive the Google OAuth round-trip.
    captureMarketingParams(searchParams);

    // Cancellation guard: this effect can run more than once (StrictMode double-mount,
    // dep changes), leaving a stale fetchAuthSession promise that navigates AFTER this
    // component has unmounted. That late navigate("/home") was overriding VerticalNav's
    // no-org redirect to /home/ai-analyst, stranding group-less users on the dashboard.
    let cancelled = false;

    fetchAuthSession()
      .then((authSession) => {
        if (cancelled) return;
        if (authSession?.tokens?.idToken) {
          const details = {
            groups: authSession?.tokens?.idToken?.payload?.["cognito:groups"],
            email: authSession?.tokens?.idToken?.payload?.email,
            username: authSession?.tokens?.idToken?.payload?.preferred_username,
          };
          dispatch(setUserDetails(details));
          const { to, options } = getPostAuthDestination();
          navigate(to, options);
        } else {
          dispatch(logOutUser({}));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.log("Err", err);
        dispatch(logOutUser({}));
      });

    return () => {
      cancelled = true;
    };
  }, [ dispatch, navigate, searchParams ]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupErrors([]);
    setConfirmationErrors([]);
    setConfirmationStatus("");
    
    const validationErrors: string[] = [];
    
    if (!signupForm.email.trim()) {
      validationErrors.push("Email is required");
    }
    if (!signupForm.username.trim()) {
      validationErrors.push("Username is required");
    } else if (signupForm.username.trim().length < 3) {
      validationErrors.push("Username must be at least 3 characters");
    }
    if (signupForm.password.length < 8) {
      validationErrors.push("Password must have at least 8 characters");
    }
    if (signupForm.password !== signupForm.confirmPassword) {
      validationErrors.push("Passwords do not match");
    }
    // Terms & Conditions validation temporarily disabled until modal is wired up
    if (validationErrors.length > 0) {
      setSignupErrors(validationErrors);
      return;
    }

    setSignupLoading(true);
    try {
      await signUp({
        username: signupForm.email,
        password: signupForm.password,
        options: {
          userAttributes: {
            preferred_username: signupForm.username.trim(),
            email: signupForm.email,
          },
        },
      });

      setPendingSignUpCredentials({
        email: signupForm.email,
        password: signupForm.password,
      });
      setShowConfirmation(true);
    } catch (err: any) {
      const rawError = err.toString();
      let errorMessage: string;

      if (rawError.toLowerCase().includes("signups are currently disabled")) {
        errorMessage = "Erode AI is not accepting new users at this time. Feel free to reach out to support@erode.ai";
      } else if (rawError.toLowerCase().includes("added to the waitlist")) {
        errorMessage = "New signups are temporarily closed. Your email has been added to the waitlist.";
        setSignupBlocked(true);
      } else {
        errorMessage = rawError.replace(`${err.code}:`, "");
      }

      setSignupErrors([errorMessage]);
    } finally {
      setSignupLoading(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setConfirmationLoading(true);
    setConfirmationErrors([]);
    setConfirmationStatus("");
    
    if (!pendingSignUpCredentials.email) {
      setConfirmationErrors(["Please restart the signup flow to request a new code."]);
      setConfirmationLoading(false);
      return;
    }

    try {
      await confirmSignUp({
        username: pendingSignUpCredentials.email,
        confirmationCode: confirmationCode,
      });

      await signIn({
        username: pendingSignUpCredentials.email,
        password: pendingSignUpCredentials.password,
      });

      // PostHog analytics: track signup success and identify user
      const authSession = await fetchAuthSession();
      const details = {
        groups: authSession?.tokens?.idToken?.payload?.["cognito:groups"],
        email: authSession?.tokens?.idToken?.payload?.email,
        username: authSession?.tokens?.idToken?.payload?.preferred_username,
      };
      const isAdmin = (details?.groups as string[] | undefined)?.includes("Admin") ?? false;
      capture(POSTHOG_EVENTS.SIGNUP_SUCCESS, { method: "email" }, { isAdmin });

      const payload = authSession?.tokens?.idToken?.payload;
      const name = (payload?.name as string) ?? details.username;
      identifyUserForPostHog(posthog, authSession.userSub ?? "", {
        email: details.email as string,
        name,
        isAdmin,
        authProvider: "cognito",
      });

      const { to, options } = getPostAuthDestination();
      navigate(to, options);
    } catch (err: any) {
      const errorMessage = err.toString().replace(`AuthError:`, "").replace(`${err.code}:`, "");
      setConfirmationErrors([errorMessage]);
    } finally {
      setConfirmationLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginErrors([]);
    
    if (!loginForm.email || !loginForm.password) {
      setLoginErrors(["Please enter both email and password"]);
      return;
    }

    setLoginLoading(true);
    try {
      const signInResponse = await signIn({
        username: loginForm.email,
        password: loginForm.password,
      });
      
      if (!signInResponse.isSignedIn) {
        if (signInResponse?.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
          throw new Error("Please confirm your signup first");
        }
      }

      const authSession = await fetchAuthSession();
      const details = {
        groups: authSession?.tokens?.idToken?.payload?.["cognito:groups"],
        email: authSession?.tokens?.idToken?.payload?.email,
        username: authSession?.tokens?.idToken?.payload?.preferred_username,
      };

      dispatch(setUserDetails(details));

      // PostHog analytics: track login success and identify user
      const isAdmin = (details?.groups as string[] | undefined)?.includes("Admin") ?? false;
      capture(POSTHOG_EVENTS.LOGIN_SUCCESS, { method: "email" }, { isAdmin });

      const payload = authSession?.tokens?.idToken?.payload;
      const name = (payload?.name as string) ?? details.username;
      const identities = payload?.identities;
      let authProvider = "cognito";
      if (identities) {
        try {
          const arr = typeof identities === "string" ? JSON.parse(identities) : identities;
          if (Array.isArray(arr) && arr[0]?.providerName) authProvider = arr[0].providerName;
        } catch {
          // keep cognito
        }
      }
      identifyUserForPostHog(posthog, authSession.userSub ?? "", {
        email: details.email as string,
        name,
        isAdmin,
        authProvider,
      });

      if (details.username) {
        const { to, options } = getPostAuthDestination();
        navigate(to, options);
      }
    } catch (err: any) {
      let errorMessage;
      if (err.name === "InvalidParameterException") {
        errorMessage = "Password value is empty.";
      } else if (err.code === "UserNotFoundException" || err.code === "NotAuthorizedException") {
        // Use the same message for both cases to prevent email enumeration attacks
        errorMessage = "Incorrect email or password. Please try again.";
      } else if (
        err.name === "NetworkError" ||
        err.message?.toLowerCase().includes("network error")
      ) {
        errorMessage = "We couldn't reach the server. Please check your connection and try again.";
      } else {
        errorMessage = err.toString().replace(`${err.name}:`, "");
      }
      setLoginErrors([errorMessage]);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingSignUpCredentials.email) {
      setConfirmationErrors(["Unable to resend the code. Please restart the signup process."]);
      return;
    }
    try {
      await resendSignUpCode({ username: pendingSignUpCredentials.email });
      setConfirmationStatus("Verification code has been resent to your email");
      setConfirmationErrors([]);
    } catch (e) {
      console.log("Resend error", e);
      setConfirmationErrors(["Failed to resend code. Please try again."]);
      setConfirmationStatus("");
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordErrors([]);
    setForgotPasswordLoading(true);
    
    try {
      await resetPassword({ username: forgotPasswordForm.email });
      setResetPasswordEmail(forgotPasswordForm.email);
      setForgotPasswordForm({ email: "" });
      setResetPasswordForm({
        code: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      setShowForgotPassword(false);
      setResetPasswordErrors([]);
      setShowResetPassword(true);
    } catch (err: any) {
      let errorMessage;
      if (err.code === "UserNotFoundException") {
        errorMessage = "We couldn't find an account with that email. Please double-check or sign up.";
      } else if (
        err.code === "InvalidParameterException" ||
        err.message?.toLowerCase().includes("username/client id")
      ) {
        errorMessage = "Email address was not found.";
      } else if (
        err.name === "NetworkError" ||
        err.message?.toLowerCase().includes("network error")
      ) {
        errorMessage = "We couldn't reach the server. Please check your connection and try again.";
      } else {
        errorMessage = err.toString().replace(`${err.name}:`, "");
      }
      setForgotPasswordErrors([errorMessage]);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetPasswordErrors([]);
    
    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmNewPassword) {
      setResetPasswordErrors(["Passwords do not match"]);
      return;
    }
    
    setResetPasswordLoading(true);
    
    try {
      await confirmResetPassword({
        username: resetPasswordEmail,
        newPassword: resetPasswordForm.newPassword,
        confirmationCode: resetPasswordForm.code,
      });
      
      await signIn({
        username: resetPasswordEmail,
        password: resetPasswordForm.newPassword,
      });

      const { to, options } = getPostAuthDestination();
      navigate(to, options);
    } catch (err: any) {
      let errorMessage;
      if (
        err.name === "NetworkError" ||
        err.message?.toLowerCase().includes("network error")
      ) {
        errorMessage = "We couldn't reach the server. Please check your connection and try again.";
      } else {
        errorMessage = err.toString().replace(`${err.name}:`, "");
      }
      setResetPasswordErrors([errorMessage]);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleOpenForgotPassword = () => {
    setForgotPasswordErrors([]);
    setLoginErrors([]);
    setForgotPasswordForm({ email: "" });
    setShowForgotPassword(true);
    resetPasswordVisibility();
  };

  const handleForgotPasswordBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordErrors([]);
    resetPasswordVisibility();
  };

  const handleResetPasswordBackToLogin = () => {
    setShowResetPassword(false);
    setShowForgotPassword(false);
    setResetPasswordErrors([]);
    resetPasswordVisibility();
  };

  const resetPasswordVisibility = () => {
    setShowLoginPassword(false);
    setShowSignupPassword(false);
    setShowSignupConfirmPassword(false);
    setShowResetNewPassword(false);
    setShowResetConfirmPassword(false);
  };

  if (user.status !== authenticationState.unAuthenticated) {
    return null;
  }

  // Forgot Password Screen
  if (showForgotPassword) {
    return (
      <div className="min-h-screen lg:h-screen flex flex-col lg:flex-row bg-eai-bg-lt2 bg-pattern-01 bg-center bg-cover">
        <AuthHeroSection />

        <div className="flex-1 w-full flex justify-center items-start p-6 sm:p-8 lg:overflow-y-auto lg:h-screen">
          <div className="w-full max-w-lg lg:my-auto">
            <MobileHeader />
            <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 sm:p-8">
              <div className="hidden lg:flex mb-6">
                <ErodeLogoBlack className="w-36 h-16" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Forgot Password</h2>
              <p className="text-gray-600 mb-8">
                Enter your email address and we'll send you a reset code.
              </p>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <input
                    type="email"
                    placeholder="Email"
                    value={forgotPasswordForm.email}
                    onChange={(e) => setForgotPasswordForm({...forgotPasswordForm, email: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors"
                    required
                  />
                </div>

                {forgotPasswordErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                    {forgotPasswordErrors.map((error, idx) => (
                      <p key={idx} className="text-red-600 text-sm">{error}</p>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="w-full py-3 bg-eai-primary-accent hover:bg-eai-accent-dk text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {forgotPasswordLoading ? "Sending..." : "Send Reset Code"}
                </button>

                <button
                  type="button"
                  onClick={handleForgotPasswordBackToLogin}
                  className="w-full py-3 bg-transparent border border-gray-300 hover:bg-gray-50 text-gray-900 font-semibold rounded-lg transition-colors"
                >
                  Back to Login
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset Password Screen
  if (showResetPassword) {
    return (
      <div className="min-h-screen lg:h-screen flex flex-col lg:flex-row bg-eai-bg-lt2 bg-pattern-01 bg-center bg-cover">
        <AuthHeroSection />

        <div className="flex-1 w-full flex justify-center items-start p-6 sm:p-8 lg:overflow-y-auto lg:h-screen">
          <div className="w-full max-w-lg lg:my-auto">
            <MobileHeader />
            <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 sm:p-8">
              <div className="hidden lg:flex mb-6">
                <ErodeLogoBlack className="w-36 h-16" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
              <p className="text-gray-600 mb-8">
                Enter the code sent to {resetPasswordEmail} and your new password.
              </p>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Reset Code"
                    value={resetPasswordForm.code}
                    onChange={(e) => setResetPasswordForm({...resetPasswordForm, code: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors"
                    required
                  />
                </div>

                <div className="relative">
                  <input
                    type={showResetNewPassword ? "text" : "password"}
                    placeholder="New Password"
                    value={resetPasswordForm.newPassword}
                    onChange={(e) => setResetPasswordForm({...resetPasswordForm, newPassword: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetNewPassword(!showResetNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    {showResetNewPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                  </button>
                </div>

                <div className="relative">
                  <input
                    type={showResetConfirmPassword ? "text" : "password"}
                    placeholder="Confirm New Password"
                    value={resetPasswordForm.confirmNewPassword}
                    onChange={(e) => setResetPasswordForm({...resetPasswordForm, confirmNewPassword: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    {showResetConfirmPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                  </button>
                </div>

                {resetPasswordErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                    {resetPasswordErrors.map((error, idx) => (
                      <p key={idx} className="text-red-600 text-sm">{error}</p>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={resetPasswordLoading}
                  className="w-full py-3 bg-eai-primary-accent hover:bg-eai-accent-dk text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {resetPasswordLoading ? "Resetting..." : "Reset Password"}
                </button>

                <button
                  type="button"
                  onClick={handleResetPasswordBackToLogin}
                  className="w-full py-3 bg-transparent border border-gray-300 hover:bg-gray-50 text-gray-900 font-semibold rounded-lg transition-colors"
                >
                  Back to Login
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showConfirmation) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row bg-eai-bg-lt2 bg-pattern-01 bg-center bg-cover">
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center relative p-3">
          <div className="relative w-full h-full">
            <div className="absolute inset-3 bg-gradient-to-br from-eai-accent-lt via-eai-primary to-eai-bg-lt2 rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-pattern-01 opacity-20"></div>
              <div className="absolute top-0 left-0 right-0 p-12 z-10">
                <ErodeLogoBlack className="w-40 h-20" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-12 z-10">
                <h1 className="text-5xl font-bold text-white mb-4">Almost There!</h1>
                <p className="text-xl text-white/90">Check your email for the verification code</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8">
              <ErodeLogoBlack className="w-32 h-16 mx-auto mb-4" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
            <p className="text-gray-600 mb-8">
              We sent a verification code to {pendingSignUpCredentials.email}
            </p>

            <form onSubmit={handleConfirmSignUp} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-eai-primary-accent transition-colors"
                  placeholder="Enter 6-digit code"
                  required
                />
              </div>

              {confirmationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                  {confirmationErrors.map((error, idx) => (
                    <p key={idx} className="text-red-600 text-sm">{error}</p>
                  ))}
                </div>
              )}

              {confirmationStatus && (
                <div className="bg-green-50 border border-green-300 rounded-lg p-4 text-green-700 text-sm">
                  {confirmationStatus}
                </div>
              )}

              <button
                type="submit"
                disabled={confirmationLoading}
                className="w-full py-3 bg-eai-primary-accent hover:bg-eai-accent-dk text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {confirmationLoading ? "Verifying..." : "Verify Email"}
              </button>

              <button
                type="button"
                onClick={handleResendCode}
                className="w-full py-3 bg-white border border-eai-primary-accent text-eai-primary-accent hover:bg-eai-primary-accent hover:text-white font-semibold rounded-lg transition-colors"
              >
                Resend Code
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen lg:h-screen flex flex-col lg:flex-row bg-eai-bg-lt2 bg-pattern-01 bg-center bg-cover">
      <AuthHeroSection />

      <div className="flex-1 w-full flex justify-center items-start p-6 sm:p-8 lg:overflow-y-auto lg:h-screen">
        <div className="w-full max-w-lg lg:my-auto">
          <MobileHeader />
          <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="hidden lg:flex mb-6">
              <ErodeLogoBlack className="w-36 h-16" />
            </div>
            <div className="flex gap-4 mb-8 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => {
                  setActiveTab("login");
                  setSignupErrors([]);
                  resetPasswordVisibility();
                }}
                className={`flex-1 py-2 px-4 rounded-md font-semibold transition-all ${
                  activeTab === "login"
                    ? "bg-eai-primary-accent text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Login
              </button>
              <button
                onClick={() => {
                  setActiveTab("signup");
                  setLoginErrors([]);
                  resetPasswordVisibility();
                }}
                className={`flex-1 py-2 px-4 rounded-md font-semibold transition-all ${
                  activeTab === "signup"
                    ? "bg-eai-primary-accent text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Sign Up
              </button>
            </div>

            {activeTab === "login" ? (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h2>
              <p className="text-gray-600 mb-8">Don't have an account? <button onClick={() => {
                setActiveTab("signup");
                setLoginErrors([]);
                resetPasswordVisibility();
              }} className="text-eai-accent-dk hover:text-eai-dark font-semibold">Sign up</button></p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <input
                    type="email"
                    placeholder="Email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors"
                  />
                </div>

                <div className="relative">
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    placeholder="Password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    aria-label={showLoginPassword ? "Hide password" : "Show password"}
                    aria-pressed={showLoginPassword}
                  >
                    {showLoginPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                  </button>
                </div>

                <div className="flex justify-end">
                  <button 
                    type="button"
                    onClick={handleOpenForgotPassword}
                    className="text-sm text-eai-accent-dk hover:text-eai-dark font-medium">
                    Forgot password?
                  </button>
                </div>

                {loginErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                    {loginErrors.map((error, idx) => (
                      <p key={idx} className="text-red-600 text-sm">{error}</p>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full py-3 bg-eai-primary-accent hover:bg-eai-accent-dk text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {loginLoading ? "Signing In..." : "Login"}
                </button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => signInWithRedirect({ provider: "Google" })}
                  className="w-full py-3 bg-white border border-gray-300 hover:bg-gray-50 text-gray-900 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </button>
              </form>
            </div>
          ) : (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Create an account</h2>
              <p className="text-gray-600 mb-8">Already have an account? <button onClick={() => {
                setActiveTab("login");
                setSignupErrors([]);
                resetPasswordVisibility();
              }} className="text-eai-accent-dk hover:text-eai-dark font-semibold">Log in</button></p>

              <form onSubmit={handleSignUp} className="space-y-4">
                <div>
                  <input
                    type="email"
                    placeholder="Email"
                    value={signupForm.email}
                    onChange={(e) => setSignupForm({...signupForm, email: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors"
                  />
                </div>

                <div>
                  <input
                    type="text"
                    placeholder="Username"
                    value={signupForm.username}
                    onChange={(e) => setSignupForm({...signupForm, username: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors"
                  />
                </div>

                <div className="relative">
                  <input
                    type={showSignupPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={signupForm.password}
                    onChange={(e) => setSignupForm({...signupForm, password: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignupPassword(!showSignupPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    {showSignupPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                  </button>
                </div>

                <div className="relative">
                  <input
                    type={showSignupConfirmPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    value={signupForm.confirmPassword}
                    onChange={(e) => setSignupForm({...signupForm, confirmPassword: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-eai-primary-accent transition-colors pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    {showSignupConfirmPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                  </button>
                </div>

                {/* Terms & Conditions checkbox hidden until modal is wired up */}

                {signupErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                    {signupErrors.map((error, idx) => (
                      <p key={idx} className="text-red-600 text-sm">{error}</p>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={signupLoading || signupBlocked}
                  className="w-full py-3 bg-eai-primary-accent hover:bg-eai-accent-dk text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {signupLoading ? "Creating Account..." : "Create account"}
                </button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => signInWithRedirect({ provider: "Google" })}
                  disabled={signupBlocked}
                  className={`w-full py-3 bg-white border border-gray-300 text-gray-900 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${signupBlocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign up with Google
                </button>
              </form>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernAuthentication;
